/**
 * Iterative radix-2 complex FFT with cached twiddle tables.
 *
 * Everything here works on split arrays (separate real/imag Float64Arrays)
 * because that is what the rest of the pipeline wants and it avoids the
 * interleaving shuffle on every access.
 */

export function nextPow2(n: number): number {
  if (n < 1) return 1;
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

export function isPow2(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

interface Twiddles {
  cos: Float64Array;
  sin: Float64Array;
}

const twiddleCache = new Map<number, Twiddles>();

/**
 * Roots of unity for a transform of size n: cos/sin of -2*pi*k/n for k < n/2.
 * Computed directly from k (never by recurrence) so error does not accumulate
 * across the 100k+ butterflies a 3-second capture needs.
 */
function twiddles(n: number): Twiddles {
  const cached = twiddleCache.get(n);
  if (cached) return cached;
  const half = n >> 1;
  const cos = new Float64Array(half);
  const sin = new Float64Array(half);
  for (let k = 0; k < half; k++) {
    const angle = (-2 * Math.PI * k) / n;
    cos[k] = Math.cos(angle);
    sin[k] = Math.sin(angle);
  }
  const table = { cos, sin };
  twiddleCache.set(n, table);
  return table;
}

/** In-place complex FFT. Length must be a power of two. */
export function fft(re: Float64Array, im: Float64Array, inverse = false): void {
  const n = re.length;
  if (im.length !== n) throw new Error('fft: real and imaginary arrays differ in length');
  if (!isPow2(n)) throw new Error(`fft: length ${n} is not a power of two`);
  if (n === 1) return;

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }

  const { cos, sin } = twiddles(n);
  const sign = inverse ? -1 : 1; // tables hold the forward (negative) exponent

  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    const stride = n / len;
    for (let base = 0; base < n; base += len) {
      for (let j = 0, t = 0; j < half; j++, t += stride) {
        const wr = cos[t];
        const wi = sign * sin[t];
        const a = base + j;
        const b = a + half;
        const xr = re[b] * wr - im[b] * wi;
        const xi = re[b] * wi + im[b] * wr;
        re[b] = re[a] - xr;
        im[b] = im[a] - xi;
        re[a] += xr;
        im[a] += xi;
      }
    }
  }

  if (inverse) {
    const scale = 1 / n;
    for (let i = 0; i < n; i++) {
      re[i] *= scale;
      im[i] *= scale;
    }
  }
}

/** Convenience wrapper: forward FFT of a real signal, zero-padded to `size`. */
export function fftReal(x: ArrayLike<number>, size: number): { re: Float64Array; im: Float64Array } {
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  const n = Math.min(x.length, size);
  for (let i = 0; i < n; i++) re[i] = x[i];
  fft(re, im, false);
  return { re, im };
}

/**
 * Linear convolution of two real signals via a single forward FFT.
 *
 * Both signals are packed into one complex transform (z = a + i*b). Their
 * spectra are recovered from the conjugate symmetry of a real sequence:
 *
 *   A(k)B(k) = ( Z(k)^2 - conj(Z(N-k))^2 ) / 4i
 *
 * so the whole product spectrum falls out of Z without ever separating A
 * from B. Costs one forward and one inverse transform instead of three.
 */
export function convolveReal(a: ArrayLike<number>, b: ArrayLike<number>): Float64Array {
  const outLength = a.length + b.length - 1;
  if (a.length === 0 || b.length === 0) return new Float64Array(0);
  const n = nextPow2(outLength);

  const zr = new Float64Array(n);
  const zi = new Float64Array(n);
  for (let i = 0; i < a.length; i++) zr[i] = a[i];
  for (let i = 0; i < b.length; i++) zi[i] = b[i];
  fft(zr, zi, false);

  const cr = new Float64Array(n);
  const ci = new Float64Array(n);
  const quarter = 0.25;
  for (let k = 0; k < n; k++) {
    const m = k === 0 ? 0 : n - k;
    const p = zr[k], q = zi[k];
    const u = zr[m], v = -zi[m]; // conj(Z(N-k))
    // D = Z(k)^2 - conj(Z(N-k))^2
    const dr = p * p - q * q - (u * u - v * v);
    const di = 2 * p * q - 2 * u * v;
    // D / 4i  ==  (D.im - i*D.re) / 4
    cr[k] = di * quarter;
    ci[k] = -dr * quarter;
  }

  fft(cr, ci, true);
  return cr.subarray(0, outLength).slice();
}

/**
 * Cross-correlation of `signal` against `reference`, i.e. the matched filter.
 * Lag 0 sits at index `reference.length - 1`.
 *
 * Chirpr's measurement path does not use this - it deconvolves with the
 * amplitude-corrected inverse sweep instead, which is the *whitened* matched
 * filter and gives a flat impulse rather than a pink-tilted one. This is kept
 * because it is the honest reference implementation to check that against.
 */
export function crossCorrelate(signal: ArrayLike<number>, reference: ArrayLike<number>): Float64Array {
  const reversed = new Float64Array(reference.length);
  for (let i = 0; i < reference.length; i++) reversed[i] = reference[reference.length - 1 - i];
  return convolveReal(signal, reversed);
}
